import React, { useState } from "react";
import {
  Play,
  Pause,
  Square,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Zap,
  ChevronUp,
  ChevronDown,
  SkipForward,
  ChevronDown as LogToggle,
} from "lucide-react";
import { useBotEngine } from "../hooks/useBotEngine";

export default function BotCard() {
  const {
    status,
    startId,
    currentId,
    concurrency,
    successCount,
    errorCount,
    logs,
    reqPerMin,
    start,
    pause,
    stop,
    reset,
    updateConcurrency,
    updateStartId,
    goToId,
  } = useBotEngine();

  const [showLogs, setShowLogs] = useState(false);
  const [editingStartId, setEditingStartId] = useState(false);
  const [startIdInput, setStartIdInput] = useState(String(startId));
  const [jumpInput, setJumpInput] = useState("");
  const [showJump, setShowJump] = useState(false);

  const isRunning = status === "running";
  const isPaused = status === "paused";
  const isIdle = status === "idle";

  const statusConfig = {
    idle:    { label: "Parado",  color: "text-[#666666]", dot: "bg-[#444444]" },
    running: { label: "Rodando", color: "text-[#00FF94]", dot: "bg-[#00FF94] animate-pulse" },
    paused:  { label: "Pausado", color: "text-[#FFD60A]", dot: "bg-[#FFD60A]" },
    stopped: { label: "Parado",  color: "text-[#666666]", dot: "bg-[#444444]" },
  };
  const sc = statusConfig[status] || statusConfig.idle;

  return (
    <div className="bg-[#0A0A0A] border border-[#222222] mb-4">

      {/* ── Linha principal ── */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">

        {/* Ícone + status */}
        <div className="flex items-center gap-1.5 mr-1">
          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
          <span className={`text-xs font-mono ${sc.color}`}>{sc.label}</span>
        </div>

        <div className="w-px h-4 bg-[#222222]" />

        {/* ID Inicial */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[#444444] font-mono uppercase">início</span>
          {editingStartId ? (
            <input
              type="number"
              value={startIdInput}
              onChange={(e) => setStartIdInput(e.target.value)}
              onBlur={() => {
                const n = parseInt(startIdInput, 10);
                if (!isNaN(n)) updateStartId(n);
                setEditingStartId(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") { const n = parseInt(startIdInput, 10); if (!isNaN(n)) updateStartId(n); setEditingStartId(false); }
                if (e.key === "Escape") { setStartIdInput(String(startId)); setEditingStartId(false); }
              }}
              className="w-28 bg-[#111111] border border-[#00FF94] text-[#EDEDED] font-mono text-xs px-1.5 py-0.5 focus:outline-none"
              autoFocus
            />
          ) : (
            <button
              onClick={() => { setStartIdInput(String(startId)); setEditingStartId(true); }}
              className="text-xs font-mono text-[#00FF94] hover:text-white transition-colors"
            >
              {startId.toLocaleString("pt-BR")}
            </button>
          )}
        </div>

        <div className="w-px h-4 bg-[#222222]" />

        {/* ID Atual */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[#444444] font-mono uppercase">atual</span>
          <span className="text-xs font-bold font-mono text-[#EDEDED]">
            {currentId.toLocaleString("pt-BR")}
          </span>
        </div>

        <div className="w-px h-4 bg-[#222222]" />

        {/* Sucesso */}
        <div className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-[#00FF94]" />
          <span className="text-xs font-mono text-[#00FF94]">{successCount.toLocaleString()}</span>
        </div>

        {/* req/min */}
        {isRunning && (
          <>
            <div className="w-px h-4 bg-[#222222]" />
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-[#FFD60A]" />
              <span className="text-xs font-mono text-[#FFD60A]">{reqPerMin}/min</span>
            </div>
          </>
        )}

        <div className="w-px h-4 bg-[#222222]" />

        {/* Concorrência */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[#444444] font-mono uppercase">conc</span>
          <button onClick={() => updateConcurrency(concurrency - 1)} className="w-4 h-4 flex items-center justify-center text-[#555555] hover:text-[#EDEDED]">
            <ChevronDown className="w-3 h-3" />
          </button>
          <span className="text-xs font-bold font-mono text-[#00F0FF] w-5 text-center">{concurrency}</span>
          <button onClick={() => updateConcurrency(concurrency + 1)} className="w-4 h-4 flex items-center justify-center text-[#555555] hover:text-[#EDEDED]">
            <ChevronUp className="w-3 h-3" />
          </button>
          <div className="flex gap-0.5 ml-0.5">
            {[1, 3, 5, 10].map((v) => (
              <button
                key={v}
                onClick={() => updateConcurrency(v)}
                className={`text-[10px] px-1 py-0.5 font-mono border transition-colors ${
                  concurrency === v
                    ? "border-[#00F0FF] text-[#00F0FF]"
                    : "border-[#1A1A1A] text-[#444444] hover:border-[#333333] hover:text-[#666666]"
                }`}
              >{v}</button>
            ))}
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Botões de controle */}
        <div className="flex items-center gap-1.5">
          {(isIdle || isPaused) && (
            <button
              onClick={start}
              className="flex items-center gap-1 px-3 py-1 bg-[#00FF94] text-black font-mono text-xs font-bold hover:bg-[#00CC76] transition-colors"
            >
              <Play className="w-3 h-3" />
              {isPaused ? "Retomar" : "Iniciar"}
            </button>
          )}
          {isRunning && (
            <button
              onClick={pause}
              className="flex items-center gap-1 px-3 py-1 bg-[#FFD60A] text-black font-mono text-xs font-bold hover:bg-[#E5C000] transition-colors"
            >
              <Pause className="w-3 h-3" />
              Pausar
            </button>
          )}
          {(isRunning || isPaused) && (
            <button
              onClick={stop}
              className="flex items-center gap-1 px-3 py-1 border border-[#FF3B30] text-[#FF3B30] font-mono text-xs font-bold hover:bg-[#FF3B30] hover:text-white transition-colors"
            >
              <Square className="w-3 h-3" />
              Parar
            </button>
          )}
          <button
            onClick={() => setShowJump(!showJump)}
            title="Ir para ID"
            className="p-1 border border-[#222222] text-[#555555] hover:border-[#444444] hover:text-[#EDEDED] transition-colors"
          >
            <SkipForward className="w-3.5 h-3.5" />
          </button>
          {!isRunning && (
            <button
              onClick={reset}
              title="Resetar"
              className="p-1 border border-[#222222] text-[#555555] hover:border-[#444444] hover:text-[#EDEDED] transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => setShowLogs(!showLogs)}
            className="flex items-center gap-1 p-1 border border-[#222222] text-[#555555] hover:border-[#444444] hover:text-[#EDEDED] transition-colors"
            title="Logs"
          >
            <LogToggle className={`w-3.5 h-3.5 transition-transform ${showLogs ? "rotate-180" : ""}`} />
            {logs.length > 0 && (
              <span className="text-[10px] font-mono">{logs.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Barra running */}
      {isRunning && (
        <div className="h-0.5 bg-[#111111]">
          <div className="h-full bg-[#00FF94] animate-pulse" style={{ width: "100%" }} />
        </div>
      )}

      {/* Jump to ID */}
      {showJump && (
        <div className="flex gap-2 px-3 py-2 border-t border-[#1A1A1A] bg-[#050505]">
          <span className="text-[10px] text-[#444444] font-mono uppercase self-center">Ir para</span>
          <input
            type="number"
            placeholder="ID..."
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const n = parseInt(jumpInput, 10);
                if (!isNaN(n)) { goToId(n); setShowJump(false); setJumpInput(""); }
              }
            }}
            className="flex-1 bg-[#111111] border border-[#333333] text-[#EDEDED] font-mono text-xs px-2 py-1 focus:outline-none focus:border-[#00F0FF]"
          />
          <button
            onClick={() => { const n = parseInt(jumpInput, 10); if (!isNaN(n)) { goToId(n); setShowJump(false); setJumpInput(""); } }}
            className="px-3 py-1 bg-[#00F0FF] text-black font-mono text-xs font-bold hover:bg-[#00D0E0] transition-colors"
          >Ir</button>
          <button onClick={() => setShowJump(false)} className="px-2 py-1 border border-[#333333] text-[#666666] font-mono text-xs hover:border-[#555555]">✕</button>
        </div>
      )}

      {/* Logs */}
      {showLogs && (
        <div className="border-t border-[#1A1A1A]">
          {logs.length === 0 ? (
            <div className="px-3 py-3 text-center">
              <span className="text-[#333333] font-mono text-xs">Nenhum log ainda.</span>
            </div>
          ) : (
            <div className="max-h-36 overflow-y-auto">
              {logs.map((log, i) => (
                <div
                  key={i}
                  className={`px-3 py-1 flex items-center gap-2 text-xs font-mono border-b border-[#0A0A0A] ${
                    log.type === "success" ? "text-[#00FF94]" : log.type === "skip" ? "text-[#444444]" : "text-[#FF3B30]"
                  }`}
                >
                  {log.type === "success" ? (
                    <CheckCircle2 className="w-2.5 h-2.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-2.5 h-2.5 flex-shrink-0" />
                  )}
                  <span className="text-[#333333]">{log.time}</span>
                  <span className="text-[#3A3A3A]">{log.userId}</span>
                  <span>{log.type === "success" ? `@${log.username}` : log.msg}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
